import { useMemo } from 'react';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import classNames from './StudentData.module.scss';
import classes from 'classnames';
import { formatDateShort } from '@/utils/formatters';
import SVG from 'react-inlinesvg';
import {
  StudentDetails,
  UserStatusEnum,
  UnitProgress,
} from '@/school-management/types/response/studentDetails';

import UserStreak from '../UserStreak/UserStreak';
import ProgressCard from '../ProgressCard/ProgressCard';

import school from '@/assets/svg/school.svg';
import diamond from '@/assets/svg/diamond.svg';
import world from '@/assets/svg/world.svg';

interface StudentDataProps {
  data?: StudentDetails | null;
  firstLogin?: string | null;
  emitShowUnitData?: (unitId: number, unitProgresData: UnitProgress) => void;
}

const StudentData = ({ data, firstLogin, emitShowUnitData }: StudentDataProps) => {
  const { t } = useAdminTranslation();

  /** Table row (list API) or student overview API — same idea as teacher firstLoginIso + selectedStudentData */
  const rawFirstLogin =
    firstLogin ?? data?.userSessionInfo?.firstLogin ?? data?.studentData?.firstLogin ?? null;

  const formattedFirstLogin = useMemo(() => {
    if (!rawFirstLogin) return null;
    const iso = rawFirstLogin.toUpperCase().endsWith('Z') ? rawFirstLogin : `${rawFirstLogin}Z`;
    return formatDateShort(iso);
  }, [rawFirstLogin]);

  const onViewUnitDetails = (unitId: number, unitProgresData: UnitProgress) => {
    emitShowUnitData?.(unitId, unitProgresData);
  };

  const progressCards = data?.progress?.unitsProgress
    ? Object.entries(data?.progress?.unitsProgress).map(entry => {
        const [key, value] = entry;

        const topics = {
          completed: value.completedTopics < 0 ? 0 : value.completedTopics,
          total: value.totalTopics < 0 ? 0 : value.totalTopics,
        };

        const activities = {
          completed: value.completedActivities < 0 ? 0 : value.completedActivities,
          total: value.totalActivites < 0 ? 0 : value.totalActivites,
        };

        return (
          <ProgressCard
            className={classNames.progressCard}
            key={key}
            unitId={value.unitId}
            title={value.unitName}
            percentage={value.progress ?? 0}
            topics={topics}
            activities={activities}
            onClick={onViewUnitDetails}
            unitProgresData={value}
          />
        );
      })
    : null;

  const level = data?.progress?.level ?? 0;
  const totalProgressPercent =
    typeof data?.progress?.totalProgressInLevel === 'number'
      ? Math.max(0, Math.min(100, Math.round(data?.progress?.totalProgressInLevel)))
      : null;

  return (
    <div className={classNames.dataContainer}>
      <div className={classNames.info}>
        {!!data?.studentData?.schoolName && (
          <div className={classNames.box}>
            <span className={classNames.title}>{t('studentDataModal.schoolName')}</span>
            <div className={classNames.text}>
              <SVG src={school} />
              <span>{data.studentData.schoolName}</span>
            </div>
          </div>
        )}
        {data?.progress?.level != null && Number(data?.progress?.level || -1) > 0 && (
          <div className={classNames.box}>
            <span className={classNames.title}>{t('studentDataModal.level')}</span>
            <div className={classNames.text}>
              {data.progress.level ? <SVG src={diamond} /> : null}
              <span>{`${data.progress.level <= 0 ? '-' : data.progress.level} | ${data.progress.level <= 0 ? '-' : t(`levelName_${data.progress.level}`)}`}</span>
            </div>
          </div>
        )}
        {!!data?.studentData?.interfaceLanguage && (
          <div className={classNames.box}>
            <span className={classNames.title}>{t('studentDataModal.interfaceLanguage')}</span>
            <div className={classNames.text}>
              <SVG src={world} />
              <span>{t(`languages.${data.studentData.interfaceLanguage}`)}</span>
            </div>
          </div>
        )}
      </div>
      {data?.progress?.userStreak && (
        <div className={classNames.streakContainer}>
          <span className={classNames.streakTitle}>{t('studentDataModal.selfPracticeStreak')}</span>
          <UserStreak className={classNames.streak} streak={data.progress.userStreak} />
        </div>
      )}

      {totalProgressPercent !== null && level > 0 && (
        <div className={classNames.levelProgressContainer}>
          <div className={classNames.levelProgress}>
            <div className={classNames.levelProgressHeader}>
              <h3 className={classNames.levelProgressTitle}>
                {t('studentDataModal.totalProgressWithinLevel')}
              </h3>
              <span className={classNames.levelProgressLevel}>
                {t('studentDataModal.level')}: {level}
              </span>
            </div>
            <div className={classNames.levelProgressBarRow}>
              <div
                className={classNames.levelProgressTrack}
                role='progressbar'
                aria-label={t('studentDataModal.totalProgressWithinLevel').toString()}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={totalProgressPercent}
              >
                <div
                  className={classNames.levelProgressFill}
                  style={{ width: `${totalProgressPercent}%` }}
                />
              </div>
              <span className={classNames.levelProgressPercent}>{totalProgressPercent}%</span>
            </div>
            {totalProgressPercent === 100 &&
              (data?.studentData?.groupSettings?.allowIndividualAdvancement ?? true) === false && (
                <p className={classNames.completedLevelNotice}>
                  {t('studentDataModal.completedLevelNotice')}
                </p>
              )}
          </div>
        </div>
      )}

      <div
        className={classes(classNames.progress, {
          [classNames.noProgress]: !data?.progress?.unitsProgress,
        })}
      >
        {data?.progress?.unitsProgress ? (
          <>
            <div className={classNames.progressHeader}>
              <h2>{t('studentDataModal.progress')}</h2>
              {formattedFirstLogin && (
                <span className={classNames.firstLogin}>
                  {t('studentDataModal.firstLogin')}: {formattedFirstLogin}
                </span>
              )}
            </div>
            <div className={classNames.progressCards}>{progressCards}</div>
          </>
        ) : (
          <>
            {data?.studentData?.userStatus === UserStatusEnum.INACTIVE && (
              <span>{t('studentDataModal.thisStudentIsInactive')}</span>
            )}
            {data?.studentData?.userStatus !== UserStatusEnum.INACTIVE && (
              <span>{t('studentDataModal.thisStudentIsLevelTestIncomplete')}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StudentData;
